import { createTheme } from '@mui/material/styles';

export const colors = {
  default: {
    primary: '#006879',
    primaryPressed: '#005664',
    primaryDsiabled: '#A7CED3',
    secondary: '#3FA7BD',
    additional: '#DDF2F3',
    background: '#F7FAFB',
    darkGrey: '#0B1E24',
    mediumGrey: '#5B6B71',
    lightMediumGrey: '#839198',
    inputPlaceholder: '#839198',
    inputField: '#F1F6F7',
    white: '#FFFFFF',
    error: '#E5564E',
    success: '#3E9B6B',
    warning: '#E8A23D',
    gradient: 'linear-gradient(135deg, #3FA7BD 0%, #5EC6B8 100%)',
  },
};

export const sizes = {
  width: { drawer: 224 },
  drawer: { width: '224px', widthCollapsed: '76px' },
};

export const theme = createTheme(
  {
    typography: {
      fontFamily: 'Poppins, sans-serif',
      fontSize: 14,
      fontWeightLight: 400,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontSize: '2rem', lineHeight: 1.25, fontWeight: 700 },
      h2: { fontSize: '1.65rem', lineHeight: 1.3, fontWeight: 700 },
      h3: { fontSize: '1.4rem', lineHeight: 1.35, fontWeight: 700 },
      h4: { fontSize: '1.15rem', lineHeight: 1.4, fontWeight: 600 },
      h5: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 600 },
      h6: { fontSize: '.875rem', lineHeight: 1.5, fontWeight: 600 },
      body1: { fontSize: '1rem', lineHeight: 1.55 },
      body2: { fontSize: '.875rem', lineHeight: 1.5 },
    },
    palette: {
      mode: 'light',
      background: { default: '#F7FAFB', paper: '#FFFFFF' },
      text: { primary: '#0B1E24', secondary: '#5B6B71' },
      primary: { main: '#006879', contrastText: '#FFFFFF' },
      secondary: { main: '#3FA7BD', contrastText: '#FFFFFF' },
      success: { main: '#3E9B6B' },
      warning: { main: '#E8A23D' },
      error: { main: '#E5564E' },
      divider: '#E5EDEF',
    },
    shape: { borderRadius: 16 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: '#F7FAFB', color: '#0B1E24' },
          '*': { boxSizing: 'border-box' },
          a: { color: 'inherit' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: 20 },
        },
      },
      MuiCard: {
        styleOverrides: {
          // Hairline border, no shadow — content surfaces sit calmly on the
          // page instead of every box floating. Elevation is reserved for
          // things that genuinely float (menus, dialogs, popovers).
          root: {
            borderRadius: 20,
            border: '1px solid #E5EDEF',
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            borderRadius: 14,
            paddingInline: 18,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
          },
          // Flat primary — the old teal gradient read as dated everywhere.
          containedPrimary: {
            background: '#006879',
            '&:hover': {
              background: '#005664',
              boxShadow: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          // Hairline-bordered on white rather than a grey filled box — reads as
          // a control, not a form field.
          root: {
            minHeight: 44,
            borderRadius: 12,
            background: '#FFFFFF',
            '& fieldset': { borderColor: '#E5EDEF' },
            '&:hover fieldset': { borderColor: '#CFE0E3' },
            '&.Mui-focused fieldset': { borderColor: '#006879', borderWidth: 1 },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid #EDF2F3', padding: '14px 16px' },
          head: {
            color: '#5B6B71',
            fontWeight: 600,
            fontSize: '.78rem',
            letterSpacing: '.03em',
            textTransform: 'uppercase',
          },
        },
      },
      // Horizontal meta list used by calendar-entries.
      MuiList: {
        styleOverrides: {
          root: {
            '&.line-list': {
              display: 'flex',
              '& .MuiListItem-root': { minWidth: 90, width: 'auto' },
              '& .MuiListItem-root .MuiTypography-root': {
                color: colors.default.lightMediumGrey,
                fontWeight: 500,
                fontSize: 16,
                lineHeight: '24px',
              },
              '& .MuiListItemIcon-root': {
                minWidth: 18,
                minHeight: 18,
                marginRight: 8,
                '& svg': { color: colors.default.primary },
              },
              '& .MuiListItem-root .text-primary .MuiTypography-root': {
                color: colors.default.primary,
              },
              '& .MuiListItem-root .text-default .MuiTypography-root': {
                color: colors.default.lightMediumGrey,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 999, fontWeight: 600 } },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 20, padding: 8 } },
      },
    },
  },
  { sizes, colors },
);

export type AppTheme = typeof theme & {
  sizes: typeof sizes;
  colors: typeof colors;
};

export default theme;
